import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { CONTACT } from "@/constants/testIds";

const EASE = [0.76, 0, 0.24, 1];
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROJECT_TYPES = [
  "Hotel / Resort",
  "Restaurant / Bar",
  "Members Club",
  "Wellness / Bathhouse",
  "Residence",
  "Brand / Concept",
  "Other",
];

// "Contact" (say hello) and "Begin a Project" open the same section
// with distinct heading/copy so intent stays clear.
const INTENT_COPY = {
  project: {
    heading: (
      <>
        Work With <span className="text-white">Us</span>
      </>
    ),
    sub: "Interested in project collaboration or venture alignment? Send us your site, story, or the shape of an idea and we\u2019ll be in touch.",
  },
  hello: {
    heading: (
      <>
        Say <span className="text-white">Hello</span>
      </>
    ),
    sub: "We\u2019re always excited to meet new friends, creatives and collaborators. If you\u2019re good people, say hi!",
  },
};

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    project_type: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [intent, setIntent] = useState(() => sessionStorage.getItem("hd-contact-intent") || "project");

  useEffect(() => {
    const onIntent = (e) => setIntent(e.detail || "project");
    window.addEventListener("hankdavid-contact-intent", onIntent);
    return () => window.removeEventListener("hankdavid-contact-intent", onIntent);
  }, []);

  const copy = INTENT_COPY[intent] || INTENT_COPY.project;

  const on = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      setDone(true);
      toast.success("Your note has been received.");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not send. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Please check your inputs.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid={CONTACT.root}
      className="relative bg-[#0a0a0b] text-white border-t border-white/[0.06]"
    >
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 px-6 md:px-10 pt-32 md:pt-48 pb-24 md:pb-40">
        {/* Left */}
        <div className="md:col-span-5 lg:col-span-5">
          <div className="flex items-center gap-4 mb-8">
            <span className="mono-label">§ Contact</span>
            <div className="hairline flex-1 max-w-24" />
          </div>
          <h2 className="font-display !normal-case text-[10vw] md:text-[5.5vw] lg:text-[5vw] leading-[0.92] tracking-[-0.02em] whitespace-nowrap">
            <span className="line-mask">
              <motion.span
                key={intent}
                initial={{ y: "110%" }}
                whileInView={{ y: "0%" }}
                viewport={{ once: true, margin: "-15%" }}
                transition={{ duration: 1.0, ease: EASE }}
              >
                {copy.heading}
              </motion.span>
            </span>
          </h2>

          <p className="mt-8 md:mt-10 text-white/60 text-sm md:text-lg max-w-md leading-relaxed">
            {copy.sub}
          </p>

          <div className="mt-14 space-y-6">
            <div>
              <div className="mono-label !normal-case">general</div>
              <a href="mailto:info@hankdavid.com" className="mt-2 inline-block font-display !normal-case text-2xl md:text-3xl hd-link" data-cursor="MAIL">
                info@hankdavid.com
              </a>
            </div>
            <div>
              <div className="mono-label !normal-case">Locations</div>
              <div className="mt-2 font-display !normal-case text-xl md:text-2xl">
                Detroit &nbsp;|&nbsp; Miami &nbsp;|&nbsp; Portland
              </div>
            </div>
          </div>
        </div>

        {/* Right, form */}
        <div className="md:col-span-7 lg:col-span-6 md:col-start-6 lg:col-start-7">
          {done ? (
            <motion.div
              data-testid={CONTACT.success}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: EASE }}
              className="border border-white/10 p-8 md:p-12"
            >
              <span className="mono-label">Received</span>
              <h3 className="font-display text-3xl md:text-5xl mt-6 leading-tight">
                Thank you, {form.name.split(" ")[0] || "friend"}.
              </h3>
              <p className="text-white/60 mt-6 max-w-md">
                We&rsquo;ve filed your note. Someone from the studio will be in touch shortly.
              </p>
            </motion.div>
          ) : (
            <form
              data-testid={CONTACT.form}
              onSubmit={submit}
              className="space-y-2"
              aria-label="Contact HANKDAVID studio"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
                <div className="py-2">
                  <label className="mono-label" htmlFor="c-name">Name</label>
                  <input
                    id="c-name"
                    data-testid={CONTACT.name}
                    className="hd-input"
                    value={form.name}
                    onChange={on("name")}
                    placeholder="Your name"
                    required
                  />
                </div>
                <div className="py-2">
                  <label className="mono-label" htmlFor="c-email">Email</label>
                  <input
                    id="c-email"
                    data-testid={CONTACT.email}
                    type="email"
                    className="hd-input"
                    value={form.email}
                    onChange={on("email")}
                    placeholder="you@studio.com"
                    required
                  />
                </div>
                <div className="py-2">
                  <label className="mono-label" htmlFor="c-company">Company</label>
                  <input
                    id="c-company"
                    data-testid={CONTACT.company}
                    className="hd-input"
                    value={form.company}
                    onChange={on("company")}
                    placeholder="Optional"
                  />
                </div>
                {intent !== "hello" && (
                  <div className="py-2">
                    <label className="mono-label" htmlFor="c-type">Project Type</label>
                    <select
                      id="c-type"
                      data-testid={CONTACT.projectType}
                      className="hd-input"
                      value={form.project_type}
                      onChange={on("project_type")}
                      style={{ appearance: "none" }}
                    >
                      <option value="" style={{ background: "#0a0a0b" }}>Select…</option>
                      {PROJECT_TYPES.map((t) => (
                        <option key={t} value={t} style={{ background: "#0a0a0b" }}>{t}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              <div className="py-2">
                <label className="mono-label" htmlFor="c-msg">Note</label>
                <textarea
                  id="c-msg"
                  data-testid={CONTACT.message}
                  className="hd-input"
                  value={form.message}
                  onChange={on("message")}
                  rows={4}
                  placeholder={intent === "hello" ? "Tell us about yourself. We look forward to connecting." : "Tell us about your site, your story, or an idea…"}
                  required
                />
              </div>

              <div className="pt-8 flex items-center justify-end border-t border-white/[0.08] mt-6">
                <button
                  data-testid={CONTACT.submit}
                  data-cursor="SEND"
                  type="submit"
                  disabled={submitting}
                  className="group flex items-center gap-4 font-display !normal-case text-2xl md:text-3xl border-b border-white/40 hover:border-white pb-2 transition-colors disabled:opacity-40"
                >
                  <span>{submitting ? "Sending…" : "Send"}</span>
                  <motion.span
                    animate={submitting ? {} : { x: [0, 6, 0] }}
                    transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                  >
                    →
                  </motion.span>
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
